import React from 'react';
import { useTranslation } from "@/lib/i18n";
import {
  Grid, Magnet, Crosshair, Target, Box, Square
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { FULL_SMALL_GLASS_SIZE, FULL_LARGE_GLASS_SIZE } from '@/constants/editor';

const renderTooltipButton = ({
  children,
  label,
  onClick,
  theme,
  isActive = false,
  className = '',
}) => {

  const activeClasses = isActive
    ? (theme === 'dark' ? 'bg-indigo-600 text-white hover:bg-indigo-500' : 'bg-indigo-600 text-white hover:bg-indigo-700')
    : (theme === 'dark' ? 'text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-200');

  return (
    <Tooltip key={label}>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={onClick}
          className={`w-8 h-8 flex items-center justify-center rounded transition-all ${activeClasses} ${className}`}
        >
          {children}
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className={`flex items-center gap-2 border shadow-xl ${
        theme === 'dark' ? 'bg-zinc-800 border-zinc-700 text-zinc-200' : 'bg-white border-slate-200 text-slate-800'
      }`}>
        <span>{label}</span>
      </TooltipContent>
    </Tooltip>
  );
};

export default function SettingsPanel({
  theme = 'dark',
  onThemeChange,
  glassType,
  setGlassType,
  showGrid,
  setShowGrid,
  showCrosshair,
  setShowCrosshair,
  snapToGuides,
  setSnapToGuides,
  onCenterView,
  viewMode,
  setViewMode,
}) {
  const { t } = useTranslation('editor');

  return (
    <TooltipProvider delayDuration={150}>
      <div className={`flex items-center justify-between p-1.5 rounded-md border mb-1 ${
        theme === 'dark' ? 'bg-zinc-950/50 border-zinc-800' : 'bg-slate-50 border-slate-100'
      }`}>
        <div className="flex flex-col gap-2 w-full">
          <div className="flex items-center gap-1 w-full justify-around border-b pb-1.5 mb-0.5 border-zinc-800/50">
            {renderTooltipButton({
              label: t('sidebar.canvas_settings.center'),
              onClick: onCenterView,
              theme,
              children: <Target className="w-3.5 h-3.5"/>,
            })}
            {renderTooltipButton({
              label: t('sidebar.canvas_settings.view_mode'),
              onClick: () => setViewMode?.(viewMode === 'normal' ? 'vector' : 'normal'),
              theme,
              isActive: viewMode === 'vector',
              children: viewMode === 'vector' ? <Square className="w-3.5 h-3.5" /> : <Box className="w-3.5 h-3.5" />,
            })}
            {renderTooltipButton({
              label: t('sidebar.canvas_settings.snap'),
              onClick: () => setSnapToGuides?.(!snapToGuides),
              theme,
              isActive: snapToGuides,
              children: <Magnet className="w-3.5 h-3.5"/>,
            })}
            {renderTooltipButton({
              label: t('sidebar.canvas_settings.grid'),
              onClick: () => setShowGrid?.(!showGrid),
              theme,
              isActive: showGrid,
              children: <Grid className="w-3.5 h-3.5"/>,
            })}
            {renderTooltipButton({
              label: t('sidebar.canvas_settings.crosshair'),
              onClick: () => setShowCrosshair?.(!showCrosshair),
              theme,
              isActive: showCrosshair,
              children: <Crosshair className="w-3.5 h-3.5"/>,
            })}
          </div>

          <div className="flex items-center gap-2 w-full justify-between">
            <div className="flex gap-0.5 flex-1">
              <button
                onClick={() => setGlassType?.(FULL_LARGE_GLASS_SIZE)}
                title={FULL_LARGE_GLASS_SIZE}
                className={`flex-1 py-0.5 rounded text-[9px] transition font-medium ${
                  glassType === FULL_LARGE_GLASS_SIZE
                    ? 'bg-amber-500 text-white shadow-sm'
                    : `${theme === 'dark' ? 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700' : 'bg-slate-200 text-slate-600 hover:bg-slate-300'}`
                }`}
              >
                {FULL_LARGE_GLASS_SIZE}
              </button>
              <button
                onClick={() => setGlassType?.(FULL_SMALL_GLASS_SIZE)}
                title={FULL_SMALL_GLASS_SIZE}
                className={`flex-1 py-0.5 rounded text-[9px] transition font-medium ${
                  glassType === FULL_SMALL_GLASS_SIZE
                    ? 'bg-amber-500 text-white shadow-sm'
                    : `${theme === 'dark' ? 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700' : 'bg-slate-200 text-slate-600 hover:bg-slate-300'}`
                }`}
              >
                {FULL_SMALL_GLASS_SIZE}
              </button>
              <button
                onClick={() => setGlassType?.('none')}
                title={t('sidebar.glass_none')}
                className={`flex-1 py-0.5 rounded text-[9px] transition font-medium ${
                  glassType === 'none'
                    ? 'bg-indigo-600 text-white shadow-sm'
                    : `${theme === 'dark' ? 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700' : 'bg-slate-200 text-slate-600 hover:bg-slate-300'}`
                }`}
              >
                Ø
              </button>
            </div>

            <select
              className={`w-16 border rounded px-0.5 py-0.5 text-[9px] focus:outline-none focus:border-indigo-500 cursor-pointer font-medium ${
                theme === 'dark' 
                  ? 'bg-zinc-900 border-zinc-700 text-zinc-200' 
                  : 'bg-slate-100 border-slate-300 text-slate-800'
              }`}
              value={theme}
              onChange={(e) => onThemeChange?.(e.target.value)}
            >
              <option value="dark">{t('sidebar.canvas_settings.theme_dark')}</option>
              <option value="light">{t('sidebar.canvas_settings.theme_light')}</option>
            </select>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}